

import { loadSwiperModule } from "../../core/base/swiper-loader.js";

export function initCollabSection() {
  const SECTION = document.querySelector("#section-collab");
  if (!SECTION) return;

  const STAGE = SECTION.querySelector(".collab-stage");
  const BOXAREA = SECTION.querySelector(".collab-box-area");
  const CARDS = Array.from(SECTION.querySelectorAll(".collab-box"));
  const STAGGER_CLASS = "collab-box-stagger";
  const LEGACY_CARD_ANI_CLASSES = [
    "ani-fade-in-up",
    "ani-md-fade-in-up",
    "ani-delay-01",
    "ani-delay-02",
    "ani-delay-03",
    "ani-delay-04",
    "ani-delay-05",
    "ani-delay-06",
    "ani-delay-07",
    "ani-delay-08",
    "ani-delay-09",
    "ani-delay-10"
  ];
  const LEGACY_PANEL_ANI_CLASSES = [
    "ani-overlay-on-inview",
    "ani-when-inview",
    "ani-xslow"
  ];

  if (!STAGE || !BOXAREA || CARDS.length === 0) {
    console.warn("[Collab] Required elements missing");
    return;
  }

  const MOBILE_MAX = 767;
  const rafHandles = new WeakMap();
  const timerHandles = new WeakMap();
  const cardTextTimers = new WeakMap();
  const cardTextPlayed = new WeakSet();
  const firstCountPlayed = new WeakSet();
  const mobileCardCountPlayed = new Set();
  const mobileChipCountPlayed = new Set();
  let mobileFirstCardEntered = false;
  let mobileInitialCountsPlayed = false;
  let sectionInviewObserver = null;
  let sectionScrollFallbackBound = false;
  const cardHoverHandlers = [];
  const cardInviewHandlers = [];
  const swiperState = {
    instance: null,
    chipFilter: null,
    chipButtons: [],
    chipHandlers: [],
    loadingPromise: null,
    bootstrapTicket: 0
  };

  const normalizeCardAnimations = () => {
    CARDS.forEach((card, index) => {
      card.classList.add(STAGGER_CLASS, "ani-when-inview", "ani-once");
      LEGACY_CARD_ANI_CLASSES.forEach((cls) => card.classList.remove(cls));
      card.style.setProperty("--collab-stagger-index", String(index));
      card.dataset.collabIndex = String(index + 1);

      const panel = card.querySelector(".collab-card__panel");
      if (!panel) return;
      LEGACY_PANEL_ANI_CLASSES.forEach((cls) => panel.classList.remove(cls));

      const icon = card.querySelector(".collab-card__icon");
      if (icon) icon.dataset.collabStep = String(index + 1);
    });
  };

  const toMs = (raw) => {
    const value = String(raw || "").trim();
    if (!value) return 0;
    if (value.endsWith("ms")) return Number.parseFloat(value) || 0;
    if (value.endsWith("s")) return (Number.parseFloat(value) || 0) * 1000;
    return Number.parseFloat(value) || 0;
  };

  const parseList = (raw) => String(raw || "").split(",").map(item => item.trim()).filter(Boolean);

  const getMaxAnimationWindowMs = (el) => {
    if (!el) return 0;
    const styles = window.getComputedStyle(el);
    const names = parseList(styles.animationName);
    const durations = parseList(styles.animationDuration);
    const delays = parseList(styles.animationDelay);
    const count = Math.max(names.length, durations.length, delays.length, 1);
    let max = 0;
    for (let i = 0; i < count; i += 1) {
      const name = names[i] || names[names.length - 1] || "none";
      if (name === "none") continue;
      const duration = toMs(durations[i] || durations[durations.length - 1] || "0s");
      const delay = toMs(delays[i] || delays[delays.length - 1] || "0s");
      const total = duration + delay;
      if (total > max) max = total;
    }
    return max;
  };

  const splitTextNodesToWordSpans = (rootEl) => {
    if (!rootEl || rootEl.dataset.collabSplitReady === "1") return;
    const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node = walker.nextNode();
    while (node) {
      if (node.nodeValue && /\S/.test(node.nodeValue)) textNodes.push(node);
      node = walker.nextNode();
    }

    let wordIndex = 0;
    textNodes.forEach((textNode) => {
      const value = textNode.nodeValue;
      if (!value) return;
      const fragment = document.createDocumentFragment();
      const regex = /(\S+)/g;
      let last = 0;
      let match = regex.exec(value);
      while (match) {
        const token = match[1];
        const start = match.index;
        if (start > last) fragment.appendChild(document.createTextNode(value.slice(last, start)));
        const tokenEl = document.createElement("span");
        tokenEl.className = "collab-head-word";
        tokenEl.style.setProperty("--collab-word-index", String(wordIndex));
        tokenEl.textContent = token;
        fragment.appendChild(tokenEl);
        wordIndex += 1;
        last = start + token.length;
        match = regex.exec(value);
      }
      if (last < value.length) fragment.appendChild(document.createTextNode(value.slice(last)));
      textNode.parentNode?.replaceChild(fragment, textNode);
    });

    rootEl.dataset.collabSplitReady = "1";
  };

  const prepareCardTextAnimations = () => {
    CARDS.forEach((card) => {
      const headline = card.querySelector(".collab-card-headline");
      if (headline) {
        const scopedLangBlocks = headline.querySelectorAll(".lang-de, .lang-en");
        if (scopedLangBlocks.length) {
          scopedLangBlocks.forEach(splitTextNodesToWordSpans);
        } else {
          splitTextNodesToWordSpans(headline);
        }
      }

      const bodyParagraph = card.querySelector(".collab-card-headline + p.wr-text-body");
      if (bodyParagraph) bodyParagraph.classList.add("collab-body-fade-target");

      const trailingBlocks = card.querySelectorAll(".collab-card__last");
      trailingBlocks.forEach((el) => el.classList.add("collab-last-fade-target"));
    });
  };

  const clearCardTextTimers = (card) => {
    const handles = cardTextTimers.get(card) || [];
    handles.forEach((handle) => window.clearTimeout(handle));
    cardTextTimers.delete(card);
  };

  const triggerCardTextAnimations = (idx) => {
    const card = CARDS[idx];
    if (!card) return;
    if (cardTextPlayed.has(card)) return;
    clearCardTextTimers(card);
    card.classList.remove("collab-text-head-animate", "collab-text-body-animate", "collab-text-last-animate");
    void card.offsetWidth;

    const headlineDelay = 400;
    const bodyDelay = headlineDelay + 250;
    const lastDelay = bodyDelay + 250;

    const headHandle = window.setTimeout(() => {
      card.classList.add("collab-text-head-animate");
    }, headlineDelay);
    const bodyHandle = window.setTimeout(() => {
      card.classList.add("collab-text-body-animate");
    }, bodyDelay);
    const lastHandle = window.setTimeout(() => {
      card.classList.add("collab-text-last-animate");
      cardTextPlayed.add(card);
    }, lastDelay);
    cardTextTimers.set(card, [headHandle, bodyHandle, lastHandle]);
  };

  const getCountDuration = (value) => 150 + (20 * value);

  const runCountUp = (onUpdate, targetValue) => {
    const target = Number(targetValue) || 0;
    const duration = getCountDuration(target);
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const current = progress >= 1 ? target : Math.floor(progress * target);
      onUpdate(current);
      if (progress < 1) return requestAnimationFrame(step);
      return 0;
    };

    return requestAnimationFrame(step);
  };

  const restartCount = (keyEl, onUpdate, targetValue) => {
    const prev = rafHandles.get(keyEl);
    if (prev) cancelAnimationFrame(prev);
    const pendingTimer = timerHandles.get(keyEl);
    if (pendingTimer) clearTimeout(pendingTimer);
    onUpdate(0);
    const start = () => {
      const handle = runCountUp(onUpdate, targetValue);
      rafHandles.set(keyEl, handle);
      timerHandles.delete(keyEl);
      firstCountPlayed.add(keyEl);
    };
    if (!firstCountPlayed.has(keyEl)) {
      const timer = setTimeout(start, 400);
      timerHandles.set(keyEl, timer);
      return;
    }
    start();
  };

  const shouldSkipMobileReplay = (playedSet, idx) => {
    if (window.innerWidth > MOBILE_MAX) return false;
    if (playedSet.has(idx)) return true;
    playedSet.add(idx);
    return false;
  };

  const isSectionInView = () => {
    const rect = SECTION.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.top < (vh * 0.9) && rect.bottom > (vh * 0.1);
  };

  const animateCardNumber = (idx) => {
    if (shouldSkipMobileReplay(mobileCardCountPlayed, idx)) return;
    const card = CARDS[idx];
    if (!card) return;
    const icon = card.querySelector(".collab-card__icon");
    if (!icon) return;
    const target = idx + 1;
    restartCount(icon, (value) => {
      icon.dataset.collabStep = String(value);
    }, target);
  };

  const animateChipNumber = (idx) => {
    if (shouldSkipMobileReplay(mobileChipCountPlayed, idx)) return;
    const btn = swiperState.chipButtons[idx];
    if (!btn) return;
    const target = idx + 1;
    restartCount(btn, (value) => {
      btn.textContent = String(value);
    }, target);
  };

  const animateAllVisibleNumbers = () => {
    if (window.innerWidth <= MOBILE_MAX) return;
    CARDS.forEach((card, idx) => {
      if (card.classList.contains("is-inview")) {
        animateCardNumber(idx);
        animateChipNumber(idx);
        triggerCardTextAnimations(idx);
        if (!mobileFirstCardEntered && idx === 0 && window.innerWidth <= MOBILE_MAX) {
          card.classList.add("collab-mobile-first-enter");
          mobileFirstCardEntered = true;
        }
      }
    });
  };

  const playMobileInitialCounts = () => {
    if (window.innerWidth > MOBILE_MAX) return;
    if (mobileInitialCountsPlayed) return;
    if (!isSectionInView()) return;
    for (let idx = 0; idx < CARDS.length; idx += 1) {
      animateChipNumber(idx);
      animateCardNumber(idx);
    }
    mobileInitialCountsPlayed = true;
  };

  const wireMobileInitialCountsTrigger = () => {
    if (window.innerWidth > MOBILE_MAX || mobileInitialCountsPlayed) return;
    if ("IntersectionObserver" in window) {
      if (sectionInviewObserver) return;
      sectionInviewObserver = new IntersectionObserver((entries) => {
        const shouldTrigger = entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0);
        if (!shouldTrigger) return;
        playMobileInitialCounts();
        if (!mobileInitialCountsPlayed) return;
        sectionInviewObserver?.disconnect();
        sectionInviewObserver = null;
      }, {
        threshold: 0.01,
        rootMargin: "180px 0px"
      });
      sectionInviewObserver.observe(SECTION);
      return;
    }
    if (!sectionScrollFallbackBound) {
      window.addEventListener("scroll", playMobileInitialCounts, { passive: true });
      sectionScrollFallbackBound = true;
    }
  };

  const equalizeHeights = () => {
    const items = CARDS.map((box) => ({
      box,
      card: box.querySelector(".collab-card"),
      panel: box.querySelector(".collab-card__panel")
    })).filter((entry) => entry.panel);
    items.forEach(({ box, card, panel }) => {
      box.style.height = "";
      if (card) card.style.height = "";
      panel.style.height = "";
    });
    const maxH = items.reduce((m, { panel }) => Math.max(m, panel.getBoundingClientRect().height || 0), 0);
    if (maxH > 0) {
      const h = Math.ceil(maxH);
      items.forEach(({ box, card, panel }) => {
        box.style.height = `${h}px`;
        if (card) card.style.height = `${h}px`;
        panel.style.height = `${h}px`;
      });
      BOXAREA.style.minHeight = `${h}px`;
    }
  };

  const clearHeights = () => {
    BOXAREA.style.minHeight = "";
    if (swiperState.instance?.wrapperEl) swiperState.instance.wrapperEl.style.height = "";
    CARDS.forEach(card => {
      card.style.height = "";
      const cardEl = card.querySelector(".collab-card");
      if (cardEl) cardEl.style.height = "";
      const panel = card.querySelector(".collab-card__panel");
      if (panel) panel.style.height = "";
    });
  };

  const setMobileEqualHeights = () => {
    const items = CARDS.map((box) => ({
      box,
      card: box.querySelector(".collab-card"),
      panel: box.querySelector(".collab-card__panel")
    })).filter((entry) => entry.panel);
    items.forEach(({ box, card, panel }) => {
      box.style.height = "auto";
      if (card) card.style.height = "auto";
      panel.style.height = "auto";
    });
    const maxH = items.reduce((m, { panel }) => Math.max(m, panel.getBoundingClientRect().height || 0), 0);
    if (maxH <= 0) return;
    const h = Math.ceil(maxH);
    items.forEach(({ box, card, panel }) => {
      box.style.height = `${h}px`;
      if (card) card.style.height = `${h}px`;
      panel.style.height = `${h}px`;
    });
    BOXAREA.style.minHeight = `${h}px`;
    if (swiperState.instance?.wrapperEl) swiperState.instance.wrapperEl.style.height = `${h}px`;
  };

  const buildChipFilter = () => {
    const chipFilter = document.createElement("div");
    chipFilter.className = "collab-chip-filter";
    const chipTrack = document.createElement("div");
    chipTrack.className = "collab-chip-track";
    chipFilter.appendChild(chipTrack);

    const chipButtons = CARDS.map((_, idx) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "collab-chip-btn";
      btn.dataset.collabChip = String(idx);
      btn.textContent = String(idx + 1);
      chipTrack.appendChild(btn);
      return btn;
    });

    return { chipFilter, chipButtons };
  };

  const setActiveChip = (idx) => {
    swiperState.chipButtons.forEach((btn, i) => {
      const isActive = i === idx;
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  };

  const setActiveCard = (idx) => {
    CARDS.forEach((card, i) => {
      card.classList.toggle("collab-card-is-active", i === idx);
    });
  };

  const wireNumberTriggers = () => {
    CARDS.forEach((card, idx) => {
      const panel = card.querySelector(".collab-card__panel");
      if (panel) {
        const onLeave = () => animateCardNumber(idx);
        const onFocusOut = (ev) => {
          const next = ev.relatedTarget;
          if (!(next instanceof Node) || !panel.contains(next)) animateCardNumber(idx);
        };
        panel.addEventListener("mouseleave", onLeave);
        panel.addEventListener("focusout", onFocusOut);
        cardHoverHandlers.push(() => {
          panel.removeEventListener("mouseleave", onLeave);
          panel.removeEventListener("focusout", onFocusOut);
        });
      }

      const onEnterInview = () => {
        if (window.innerWidth <= MOBILE_MAX) {
          playMobileInitialCounts();
        } else {
          animateCardNumber(idx);
          animateChipNumber(idx);
        }
        triggerCardTextAnimations(idx);
        if (!mobileFirstCardEntered && idx === 0 && window.innerWidth <= MOBILE_MAX) {
          card.classList.add("collab-mobile-first-enter");
          mobileFirstCardEntered = true;
        }
      };
      card.addEventListener("ani:inview-enter", onEnterInview);
      cardInviewHandlers.push(() => {
        card.removeEventListener("ani:inview-enter", onEnterInview);
      });
    });
  };

  const enableSwiper = () => {
    if (swiperState.instance || swiperState.loadingPromise) return;
    const ticket = ++swiperState.bootstrapTicket;
    swiperState.loadingPromise = loadSwiperModule()
      .then(({ Swiper }) => {
        if (ticket !== swiperState.bootstrapTicket) return;
        if (window.innerWidth > MOBILE_MAX) return;

        const { chipFilter, chipButtons } = buildChipFilter();
        swiperState.chipFilter = chipFilter;
        swiperState.chipButtons = chipButtons;
        STAGE.parentNode?.insertBefore(chipFilter, STAGE);

        STAGE.classList.add("swiper", "collab-swiper");
        BOXAREA.classList.add("swiper-wrapper");
        CARDS.forEach(card => card.classList.add("swiper-slide"));

        const swiper = new Swiper(STAGE, {
          slidesPerView: 1,
          spaceBetween: 16,
          autoHeight: false,
          speed: 450,
          grabCursor: true,
          centeredSlides: false,
          watchOverflow: true
        });

        if (ticket !== swiperState.bootstrapTicket) {
          swiper.destroy(true, true);
          return;
        }

        swiperState.instance = swiper;
        setActiveChip(0);
        setActiveCard(0);
        triggerCardTextAnimations(0);

        swiperState.chipHandlers = swiperState.chipButtons.map((btn, idx) => {
          const handler = () => swiper.slideTo(idx);
          btn.addEventListener("click", handler);
          return handler;
        });

        swiper.on("slideChange", () => {
          const idx = swiper.realIndex ?? swiper.activeIndex ?? 0;
          setActiveChip(idx);
          setActiveCard(idx);
          if (window.innerWidth > MOBILE_MAX) {
            animateCardNumber(idx);
            animateChipNumber(idx);
          }
          triggerCardTextAnimations(idx);
        });

        setMobileEqualHeights();
        swiper.update();
        playMobileInitialCounts();
        animateAllVisibleNumbers();
      })
      .catch((error) => {
        console.warn("[Collab] Swiper setup failed", error);
      })
      .finally(() => {
        if (ticket === swiperState.bootstrapTicket) swiperState.loadingPromise = null;
      });
  };

  const disableSwiper = () => {
    swiperState.bootstrapTicket += 1;
    swiperState.loadingPromise = null;
    if (!swiperState.instance && !swiperState.chipFilter) return;

    if (swiperState.instance) {
      swiperState.instance.destroy(true, true);
      swiperState.instance = null;
    }

    swiperState.chipButtons.forEach((btn, idx) => {
      const handler = swiperState.chipHandlers[idx];
      if (handler) btn.removeEventListener("click", handler);
    });
    swiperState.chipHandlers = [];
    swiperState.chipButtons = [];

    if (swiperState.chipFilter) {
      swiperState.chipFilter.remove();
      swiperState.chipFilter = null;
    }

    CARDS.forEach(card => card.classList.remove("swiper-slide"));
    BOXAREA.classList.remove("swiper-wrapper");
    STAGE.classList.remove("swiper", "collab-swiper");
    setActiveCard(-1);
    clearHeights();
  };

  const syncLayout = () => {
    const isMobile = window.innerWidth <= MOBILE_MAX;
    if (isMobile) {
      enableSwiper();
      setMobileEqualHeights();
      playMobileInitialCounts();
    } else {
      disableSwiper();
      equalizeHeights();
    }
  };

  normalizeCardAnimations();
  SECTION.classList.add("js-collab-text-ani");
  prepareCardTextAnimations();
  wireNumberTriggers();
  syncLayout();
  wireMobileInitialCountsTrigger();
  animateAllVisibleNumbers();

  const onResize = () => {
    syncLayout();
    wireMobileInitialCountsTrigger();
  };
  window.addEventListener("resize", onResize);
  window.addEventListener("load", onResize, { once: true });
  setTimeout(onResize, 300);


  return () => {
    disableSwiper();
    CARDS.forEach((card) => clearCardTextTimers(card));
    cardHoverHandlers.forEach((dispose) => dispose());
    cardInviewHandlers.forEach((dispose) => dispose());
    window.removeEventListener("resize", onResize);
    if (sectionInviewObserver) {
      sectionInviewObserver.disconnect();
      sectionInviewObserver = null;
    }
    if (sectionScrollFallbackBound) {
      window.removeEventListener("scroll", playMobileInitialCounts);
      sectionScrollFallbackBound = false;
    }
    window.removeEventListener("load", onResize);
  };
}
